import React, { useState, useEffect, useMemo } from 'react';
import { getMessagerieRecipients, getMessagerieHistory, sendEmail, sendSms, previewMessageApi, getProjets, getMembres } from '../services/api';
import { FiSend, FiMail, FiPhone, FiUsers, FiPaperclip, FiX, FiEye, FiHash, FiCheck, FiArrowLeft, FiSearch, FiPlus } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const VARIABLES = [
  { key: '{nom}', label: 'Nom' },
  { key: '{prenom}', label: 'Prénom' },
  { key: '{prenomnom}', label: 'Prénom & Nom' },
  { key: '{service}', label: 'Service' },
  { key: '{matricule}', label: 'Matricule' },
  { key: '{email}', label: 'Email' },
  { key: '{telephone}', label: 'Téléphone' },
  { key: '{total_cotisation}', label: 'Total cotisation' },
];

const formatDate = (d: string) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

const Messagerie = () => {
  const [tab, setTab] = useState<'compose' | 'history'>('compose');
  const [channel, setChannel] = useState<'email' | 'sms'>('email');

  // Targeting
  const [targetType, setTargetType] = useState('all');
  const [targetFilter, setTargetFilter] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<any[]>([]);
  const [recipientInfo, setRecipientInfo] = useState<any>(null);

  // External recipients
  const [externalInput, setExternalInput] = useState('');
  const [externals, setExternals] = useState<string[]>([]);

  // Data sources
  const [projets, setProjets] = useState<any[]>([]);
  const [membres, setMembres] = useState<any[]>([]);
  const [memberSearch, setMemberSearch] = useState('');

  // Message
  const [sujet, setSujet] = useState('');
  const [message, setMessage] = useState('');
  const [fichiers, setFichiers] = useState<File[]>([]);
  const quillRef = React.useRef<any>(null);

  // Preview
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);

  // Sending
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [confirmSend, setConfirmSend] = useState(false);

  // History
  const [history, setHistory] = useState<any[]>([]);

  // Quill modules config
  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ]
  }), []);

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'align', 'list', 'bullet', 'link'
  ];

  // Load data sources
  useEffect(() => {
    getProjets().then(r => setProjets(r.data)).catch(() => {});
  }, []);

  // Load members when target is individual
  useEffect(() => {
    if (targetType === 'individual') {
      getMembres({ limit: 2000 }).then(r => setMembres(r.data.membres || r.data)).catch(() => {});
    }
  }, [targetType]);

  // Load recipients count when targeting changes
  useEffect(() => {
    if (targetType === 'none') {
      setRecipientInfo(null);
      return;
    }
    if (targetType === 'individual' && selectedMembers.length === 0) {
      setRecipientInfo(null);
      return;
    }
    const filter = targetType === 'individual' ? selectedMembers.map(m => m.id).join(',') : targetFilter;
    if (targetType !== 'all' && targetType !== 'individual' && !filter) {
      setRecipientInfo(null);
      return;
    }
    getMessagerieRecipients({ type: targetType, filter })
      .then(r => setRecipientInfo(r.data))
      .catch(() => setRecipientInfo(null));
  }, [targetType, targetFilter, selectedMembers]);

  // Load history
  useEffect(() => {
    if (tab === 'history') {
      getMessagerieHistory().then(r => setHistory(r.data)).catch(() => {});
    }
  }, [tab]);

  const insertVariable = (varKey: string) => {
    if (channel === 'sms') {
      // For SMS, just append to message
      setMessage(prev => prev + varKey);
      return;
    }
    // For email (Quill), insert at cursor
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      editor.insertText(range.index, varKey);
      editor.setSelection(range.index + varKey.length);
    }
  };

  const handleFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFichiers(prev => [...prev, ...newFiles].slice(0, 5));
    }
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    setFichiers(prev => prev.filter((_, i) => i !== index));
  };

  const handlePreview = async () => {
    try {
      const userId = targetType === 'individual' && selectedMembers.length > 0 ? selectedMembers[0].id : undefined;
      const r = await previewMessageApi({ sujet, message, userId });
      setPreviewData(r.data);
      setShowPreview(true);
    } catch { }
  };

  const handleSend = async () => {
    setConfirmSend(false);
    setSending(true);
    setResult(null);

    try {
      if (channel === 'email') {
        const formData = new FormData();
        formData.append('sujet', sujet);
        formData.append('message', message);
        formData.append('destinataires_type', targetType);
        const filter = targetType === 'individual' ? selectedMembers.map(m => m.id).join(',') : targetFilter;
        formData.append('destinataires_filtre', filter);
        if (externals.length > 0) {
          formData.append('externes', JSON.stringify(externals));
        }
        fichiers.forEach(f => formData.append('fichiers', f));
        const r = await sendEmail(formData);
        setResult({ ...r.data, channel: 'email' });
      } else {
        const filter = targetType === 'individual' ? selectedMembers.map(m => m.id).join(',') : targetFilter;
        const r = await sendSms({
          message, destinataires_type: targetType, destinataires_filtre: filter,
          externes: externals.length > 0 ? externals : undefined
        });
        setResult({ ...r.data, channel: 'sms' });
      }
    } catch (err: any) {
      setResult({ success: false, error: err.response?.data?.error || "Erreur lors de l'envoi" });
    }
    setSending(false);
  };

  const toggleMember = (m: any) => {
    setSelectedMembers(prev => {
      const exists = prev.find(x => x.id === m.id);
      if (exists) return prev.filter(x => x.id !== m.id);
      return [...prev, m];
    });
  };

  const addExternal = () => {
    const val = externalInput.trim();
    if (!val) return;
    // Accept comma or semicolon separated values
    const items = val.split(/[,;]+/).map(s => s.trim()).filter(s => s.length > 0);
    const newExternals = items.filter(s => !externals.includes(s));
    if (newExternals.length > 0) {
      setExternals(prev => [...prev, ...newExternals]);
    }
    setExternalInput('');
  };

  const removeExternal = (val: string) => {
    setExternals(prev => prev.filter(e => e !== val));
  };

  const handleExternalKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addExternal();
    }
  };

  const filteredMembres = membres.filter(m => {
    if (!memberSearch) return true;
    const s = memberSearch.toLowerCase();
    return (m.prenomnom || '').toLowerCase().includes(s) ||
      (m.email || '').toLowerCase().includes(s) ||
      (m.matricule || '').toLowerCase().includes(s);
  });

  const resetForm = () => {
    setSujet('');
    setMessage('');
    setFichiers([]);
    setResult(null);
    setTargetType('all');
    setTargetFilter('');
    setSelectedMembers([]);
    setExternals([]);
    setExternalInput('');
  };

  const targetLabel = () => {
    switch (targetType) {
      case 'none': return 'Externes uniquement';
      case 'all': return 'Tous les membres';
      case 'role': return targetFilter;
      case 'projet': return projets.find(p => String(p.id) === String(targetFilter))?.libelle || 'Projet';
      case 'individual': return `${selectedMembers.length} membre(s) sélectionné(s)`;
      default: return '';
    }
  };

  const hasContent = channel === 'email'
    ? message.replace(/<[^>]*>/g, '').trim().length > 0
    : message.trim().length > 0;

  return (
    <div>
      <h1 className="page-title">Messagerie Email / SMS</h1>
      <p className="page-subtitle">Envoyez des messages personnalisés à vos membres</p>

      {/* Tabs */}
      <div className="messagerie-tabs">
        <button className={`messagerie-tab ${tab === 'compose' ? 'active' : ''}`} onClick={() => setTab('compose')}>
          <FiSend /> Nouveau message
        </button>
        <button className={`messagerie-tab ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>
          <FiHash /> Historique
        </button>
      </div>

      {tab === 'compose' && (
        <>
          {/* Result banner */}
          {result && (
            <div className={`alert ${result.success ? 'alert-success' : 'alert-error'}`}>
              {result.success ? (
                <div>
                  <strong><FiCheck /> {result.channel === 'sms' ? 'SMS' : 'Emails'} envoyé(s) avec succès !</strong>
                  <p style={{ margin: '8px 0 0' }}>
                    {result.nb_envoyes} envoyé(s) sur {result.nb_destinataires} destinataire(s)
                    {result.nb_erreurs > 0 && <span style={{ color: '#c62828' }}> — {result.nb_erreurs} erreur(s)</span>}
                  </p>
                  <button className="btn btn-outline" style={{ marginTop: 12 }} onClick={resetForm}>
                    <FiArrowLeft /> Nouveau message
                  </button>
                </div>
              ) : (
                <div><strong>Erreur :</strong> {result.error}</div>
              )}
            </div>
          )}

          {!result && (
            <div className="messagerie-grid">
              {/* LEFT: Canal & Destinataires */}
              <div className="card">
                <div className="card-header">
                  <span><FiUsers /> Canal & Destinataires</span>
                </div>
                <div className="card-body">
                  {/* Channel selector */}
                  <div className="form-group">
                    <label>Canal d'envoi</label>
                    <div className="messagerie-channel-selector">
                      <label className={`channel-option ${channel === 'email' ? 'active email' : ''}`}>
                        <input type="radio" name="channel" checked={channel === 'email'} onChange={() => setChannel('email')} />
                        <FiMail /> Email
                      </label>
                      <label className={`channel-option ${channel === 'sms' ? 'active sms' : ''}`}>
                        <input type="radio" name="channel" checked={channel === 'sms'} onChange={() => setChannel('sms')} />
                        <FiPhone /> SMS
                      </label>
                    </div>
                  </div>

                  {/* Target type */}
                  <div className="form-group">
                    <label>Ciblage des destinataires</label>
                    <select className="form-input" value={targetType} onChange={e => { setTargetType(e.target.value); setTargetFilter(''); setSelectedMembers([]); }}>
                      <option value="none">-- Aucun (externes uniquement) --</option>
                      <option value="all">Tous les membres</option>
                      <option value="projet">Par projet</option>
                      <option value="role">Par rôle</option>
                      <option value="individual">Membres spécifiques</option>
                    </select>
                  </div>

                  {/* Projet filter */}
                  {targetType === 'projet' && (
                    <div className="form-group">
                      <label>Projet</label>
                      <select className="form-input" value={targetFilter} onChange={e => setTargetFilter(e.target.value)}>
                        <option value="">-- Sélectionner un projet --</option>
                        {projets.map((p: any) => <option key={p.id} value={p.id}>{p.libelle}</option>)}
                      </select>
                    </div>
                  )}

                  {/* Role filter */}
                  {targetType === 'role' && (
                    <div className="form-group">
                      <label>Rôle</label>
                      <select className="form-input" value={targetFilter} onChange={e => setTargetFilter(e.target.value)}>
                        <option value="">-- Sélectionner un rôle --</option>
                        <option value="membre">Membre</option>
                        <option value="bureau">Bureau</option>
                        <option value="admin">Admin</option>
                        <option value="superadmin">Super Admin</option>
                      </select>
                    </div>
                  )}

                  {/* Individual selection */}
                  {targetType === 'individual' && (
                    <div className="form-group">
                      <label>Sélectionner les membres</label>
                      <div className="search-box" style={{ marginBottom: 10 }}>
                        <FiSearch className="search-icon" />
                        <input type="text" className="form-input" placeholder="Rechercher par nom, email, matricule..."
                          value={memberSearch} onChange={e => setMemberSearch(e.target.value)} style={{ paddingLeft: 38 }} />
                      </div>

                      {selectedMembers.length > 0 && (
                        <div className="messagerie-selected-tags">
                          {selectedMembers.map(m => (
                            <span key={m.id} className="member-tag">
                              {m.prenomnom}
                              <FiX onClick={() => toggleMember(m)} />
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="messagerie-member-list">
                        {filteredMembres.slice(0, 50).map(m => (
                          <div key={m.id} className={`member-item ${selectedMembers.find(x => x.id === m.id) ? 'selected' : ''}`}
                            onClick={() => toggleMember(m)}>
                            <div>
                              <strong>{m.prenomnom}</strong>
                            </div>
                            <span className="member-contact">{m.email || m.telephone || ''}</span>
                          </div>
                        ))}
                        {filteredMembres.length === 0 && (
                          <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>Aucun membre trouvé</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Recipient summary */}
                  {recipientInfo && (
                    <div className="messagerie-recipient-summary">
                      <div className="recipient-title">{targetLabel()}</div>
                      <div className="recipient-stats">
                        <span><FiUsers /> {recipientInfo.total} membre(s)</span>
                        <span><FiMail /> {recipientInfo.avecEmail} email(s)</span>
                        <span><FiPhone /> {recipientInfo.avecTelephone} tél.</span>
                      </div>
                      {recipientInfo.apercu && recipientInfo.apercu.length > 0 && (
                        <div className="recipient-preview">
                          {recipientInfo.apercu.slice(0, 5).map((u: any) => u.prenomnom).join(', ')}
                          {recipientInfo.total > 5 && ` et ${recipientInfo.total - 5} autres...`}
                        </div>
                      )}
                    </div>
                  )}

                  {/* External recipients */}
                  <div className="form-group" style={{ marginTop: 16 }}>
                    <label>{channel === 'email' ? 'Destinataires externes (emails)' : 'Destinataires externes (téléphones)'}</label>
                    <p style={{ fontSize: 12, color: '#888', margin: '2px 0 8px' }}>
                      Ajoutez des {channel === 'email' ? 'adresses email' : 'numéros de téléphone'} qui ne sont pas dans la base. Séparez par des virgules ou appuyez sur Entrée.
                    </p>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input type="text" className="form-input" style={{ flex: 1 }}
                        placeholder={channel === 'email' ? 'ex: nom@email.com' : 'ex: 771234567'}
                        value={externalInput}
                        onChange={e => setExternalInput(e.target.value)}
                        onKeyDown={handleExternalKeyDown} />
                      <button type="button" className="btn btn-primary" onClick={addExternal} style={{ whiteSpace: 'nowrap' }}>
                        <FiPlus /> Ajouter
                      </button>
                    </div>
                    {externals.length > 0 && (
                      <div className="messagerie-selected-tags" style={{ marginTop: 8 }}>
                        {externals.map(ext => (
                          <span key={ext} className="member-tag" style={{ background: '#fff3e0', color: '#e65100' }}>
                            {ext}
                            <FiX onClick={() => removeExternal(ext)} />
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* RIGHT: Message */}
              <div className="card">
                <div className="card-header">
                  <span><FiMail /> Rédiger le message</span>
                </div>
                <div className="card-body">
                  {/* Subject (email only) */}
                  {channel === 'email' && (
                    <div className="form-group">
                      <label>Objet du mail</label>
                      <input type="text" className="form-input" placeholder="Ex: Information importante COHAMAC..."
                        value={sujet} onChange={e => setSujet(e.target.value)} />
                    </div>
                  )}

                  {/* Variables toolbar */}
                  <div className="form-group">
                    <label>Variables de personnalisation</label>
                    <div className="messagerie-variables">
                      {VARIABLES.map(v => (
                        <button key={v.key} type="button" className="var-chip" onClick={() => insertVariable(v.key)}>
                          {v.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Message body - WYSIWYG for email, textarea for SMS */}
                  <div className="form-group">
                    <label>Contenu du message</label>
                    {channel === 'email' ? (
                      <div className="messagerie-editor">
                        <ReactQuill
                          ref={quillRef}
                          theme="snow"
                          value={message}
                          onChange={setMessage}
                          modules={quillModules}
                          formats={quillFormats}
                          placeholder="Bonjour {prenom} {nom},&#10;&#10;Votre cotisation totale s'élève à {total_cotisation} FCFA.&#10;&#10;Cordialement,&#10;La direction COHAMAC"
                        />
                      </div>
                    ) : (
                      <textarea className="form-input" rows={8}
                        placeholder={"Bonjour {prenom} {nom}, votre cotisation s'élève à {total_cotisation} FCFA. COHAMAC"}
                        value={message} onChange={e => setMessage(e.target.value)}
                        style={{ lineHeight: 1.6 }} />
                    )}
                  </div>

                  {/* Attachments (email only) */}
                  {channel === 'email' && (
                    <div className="form-group">
                      <label><FiPaperclip style={{ verticalAlign: 'middle' }} /> Pièces jointes (max 5 fichiers, 10 Mo chacun)</label>
                      <div className="messagerie-file-upload">
                        <label className="file-upload-btn">
                          <FiPaperclip /> Ajouter un fichier
                          <input type="file" multiple onChange={handleFileAdd} hidden
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.zip,.csv" />
                        </label>
                      </div>
                      {fichiers.length > 0 && (
                        <div className="messagerie-file-list">
                          {fichiers.map((f, i) => (
                            <div key={i} className="file-item">
                              <FiPaperclip />
                              <span className="file-name">{f.name}</span>
                              <span className="file-size">{(f.size / 1024).toFixed(0)} Ko</span>
                              <button className="file-remove" onClick={() => removeFile(i)}><FiX /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="messagerie-actions">
                    <button className="btn btn-outline" onClick={handlePreview} disabled={!hasContent}>
                      <FiEye /> Aperçu
                    </button>
                    <button className="btn btn-primary" onClick={() => setConfirmSend(true)}
                      disabled={!hasContent || (channel === 'email' && !sujet) || ((targetType !== 'none' && (!recipientInfo || recipientInfo.total === 0)) && externals.length === 0) || sending}>
                      <FiSend /> {sending ? 'Envoi en cours...' : 'Envoyer'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Preview modal */}
          {showPreview && previewData && (
            <div className="modal-overlay" onClick={() => setShowPreview(false)}>
              <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 650 }}>
                <div className="modal-header">
                  <h3>Aperçu du message personnalisé</h3>
                  <button className="modal-close" onClick={() => setShowPreview(false)}><FiX /></button>
                </div>
                <div className="modal-body">
                  <div style={{ background: '#f8f9fa', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
                    Exemple pour : <strong>{previewData.user?.prenomnom}</strong> ({previewData.user?.email})
                  </div>
                  {channel === 'email' && previewData.sujet && (
                    <div className="form-group">
                      <label>Objet</label>
                      <div style={{ padding: '10px 14px', background: '#f5f5f5', borderRadius: 8, fontWeight: 600 }}>
                        {previewData.sujet}
                      </div>
                    </div>
                  )}
                  <div className="form-group">
                    <label>Message</label>
                    <div className="messagerie-preview-body" dangerouslySetInnerHTML={{ __html: previewData.message }} />
                  </div>
                  {fichiers.length > 0 && (
                    <div style={{ fontSize: 13, color: '#666' }}>
                      <FiPaperclip /> {fichiers.length} pièce(s) jointe(s) : {fichiers.map(f => f.name).join(', ')}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline" onClick={() => setShowPreview(false)}>Fermer</button>
                </div>
              </div>
            </div>
          )}

          {/* Confirm send modal */}
          {confirmSend && (
            <div className="modal-overlay" onClick={() => setConfirmSend(false)}>
              <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 450 }}>
                <div className="modal-header">
                  <h3>Confirmer l'envoi</h3>
                  <button className="modal-close" onClick={() => setConfirmSend(false)}><FiX /></button>
                </div>
                <div className="modal-body" style={{ textAlign: 'center', padding: '30px 24px' }}>
                  <div style={{ fontSize: 52, marginBottom: 16 }}>{channel === 'email' ? '📧' : '📱'}</div>
                  <p style={{ fontSize: 15, color: '#555', marginBottom: 8 }}>
                    Vous allez envoyer {channel === 'email' ? 'un email' : 'un SMS'} à
                  </p>
                  <div style={{ fontSize: 32, fontWeight: 700, color: '#563c1b', marginBottom: 6 }}>
                    {(targetType === 'none' ? 0 : (channel === 'email' ? (recipientInfo?.avecEmail || 0) : (recipientInfo?.avecTelephone || 0))) + externals.length}
                  </div>
                  <p style={{ color: '#888', fontSize: 14, marginBottom: 0 }}>destinataire(s)</p>
                  <p style={{ color: '#666', fontSize: 13, marginTop: 4, marginBottom: 4 }}>{targetLabel()}</p>
                  {externals.length > 0 && (
                    <p style={{ color: '#e65100', fontSize: 13, marginBottom: 20 }}>
                      + {externals.length} destinataire(s) externe(s)
                    </p>
                  )}
                  {externals.length === 0 && <div style={{ marginBottom: 20 }} />}
                </div>
                <div className="modal-footer" style={{ justifyContent: 'center' }}>
                  <button className="btn btn-outline" onClick={() => setConfirmSend(false)}>Annuler</button>
                  <button className="btn btn-primary" onClick={handleSend}>
                    <FiSend /> Confirmer l'envoi
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* History tab */}
      {tab === 'history' && (
        <div className="card">
          <div className="card-header">Historique des envois</div>
          <div className="card-body">
            {history.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#999', padding: '50px 20px' }}>
                <FiMail style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }} />
                <p>Aucun envoi pour le moment</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Objet</th>
                      <th>Ciblage</th>
                      <th style={{ textAlign: 'center' }}>Destinataires</th>
                      <th style={{ textAlign: 'center' }}>Envoyés</th>
                      <th style={{ textAlign: 'center' }}>Erreurs</th>
                      <th>Envoyé par</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h: any) => (
                      <tr key={h.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(h.created)}</td>
                        <td>
                          <span className={`badge ${h.type === 'email' ? 'badge-blue' : 'badge-green'}`}>
                            {h.type === 'email' ? '📧 Email' : '📱 SMS'}
                          </span>
                        </td>
                        <td>{h.sujet || '-'}</td>
                        <td style={{ fontSize: 13 }}>
                          {h.destinataires_type === 'all' ? 'Tous les membres' : `${h.destinataires_type}: ${h.destinataires_filtre}`}
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 600 }}>{h.nb_destinataires}</td>
                        <td style={{ textAlign: 'center', color: '#2e7d32', fontWeight: 600 }}>{h.nb_envoyes}</td>
                        <td style={{ textAlign: 'center', color: h.nb_erreurs > 0 ? '#c62828' : '#999', fontWeight: h.nb_erreurs > 0 ? 600 : 400 }}>
                          {h.nb_erreurs}
                        </td>
                        <td style={{ fontSize: 13 }}>{h.created_by_name || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Messagerie;
